import { SortDirection, SortOption } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { H3 } from '@repo/ui/h3'
import { useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Label } from '@repo/ui/label'
import { type RowSelectionState, Table } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo } from 'react'

import { columns } from '@/components/admin/members/beneficiaries/columns'
import { DataTable } from '@/components/admin/members/data-table'
import { AddFilterWrapper } from '@/components/beneficiary/list/AddFilter'
import { BeneficiaryFilterSearchTextWrapper } from '@/components/beneficiary/list/BeneficiaryFilterSearchText'
import { BeneficiaryListFiltersWrapper } from '@/components/beneficiary/list/BeneficiaryListFilters'
import { BeneficiaryListPagination } from '@/components/beneficiary/list/BeneficiaryListPagination'
import { Loader } from '@/components/beneficiary/list/Loader'
import { ResetFiltersWrapper } from '@/components/beneficiary/list/ResetFilters'
import { Beneficiary } from '@/store/beneficiary'
import { reset, setSortOption } from '@/store/beneficiary-list-filters/slice'
import {
  assignCaseWorkerToBeneficiariesFromFilters,
  assignCaseWorkerToBeneficiary,
  unassignCaseWorkerFromBeneficiariesFromFilters,
  unassignCaseWorkerFromBeneficiary,
} from '@/store/case-workers/thunks'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { CaseWorker } from '@/store/user'

export function BeneficiaryListWrapper({
  caseWorker,
  onSelectBeneficiary,
}: {
  caseWorker: CaseWorker
  onSelectBeneficiary: () => void
}) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'admin.members.beneficiaryList' })
  useEffect(() => {
    dispatch(reset())
  }, [])
  const { beneficiaries, loading, sortOption } = useStoreState((state) => state.beneficiaryList)
  if (loading) return <Loader />
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 w-fit">
        <H3>{t('title')}</H3>
        <div className="flex gap-4 divide-x-2">
          <div className="flex gap-2 items-center">
            <Label>{tc('fullName', { cfl: true, colon: true })}</Label>
            <InlineCode className="text-xs">
              {caseWorker.firstName} {caseWorker.lastName}
            </InlineCode>
          </div>
          <div className="flex gap-2 items-center pl-4">
            <Label>{tc('email', { cfl: true, colon: true })}</Label>
            <InlineCode className="text-xs">{caseWorker.email}</InlineCode>
          </div>
          <div className="flex gap-2 items-center pl-4">
            <Label>{tc('role', { cfl: true, colon: true })}</Label>
            <InlineCode className="text-xs">{caseWorker.role}</InlineCode>
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-2">
        <div className="flex flex-col gap-2">
          <div className="border-primary-50 mb-4 flex items-center gap-2 border-b bg-white pb-4 shadow-sm">
            <Label className="mr-2 text-base">{tc('filter', { cfl: true, colon: true })}</Label>
            <AddFilterWrapper />
            <ResetFiltersWrapper />
          </div>
        </div>
        <div className="inline-flex flex-1 flex-wrap items-center gap-2">
          <BeneficiaryFilterSearchTextWrapper />
          <BeneficiaryListFiltersWrapper />
        </div>

        <BeneficiaryList
          beneficiaries={beneficiaries}
          caseWorker={caseWorker}
          onSelectBeneficiary={onSelectBeneficiary}
          sortOption={sortOption}
        />
        <BeneficiaryListPagination />
      </div>
    </div>
  )
}

function BeneficiaryList({
  beneficiaries,
  caseWorker,
  sortOption,
  onSelectBeneficiary,
}: {
  beneficiaries: Beneficiary[]
  caseWorker: CaseWorker
  sortOption: SortOption
  onSelectBeneficiary: () => void
}) {
  const totalCount = useStoreState((state) => state.beneficiaryList.pagination.totalItems)
  const dispatch = useStoreDispatch()
  const assignedBeneficiaries = useMemo(
    () => beneficiaries.filter((b) => b.caseWorkerIds.includes(caseWorker.id)),
    [beneficiaries, caseWorker.id],
  )

  const rowSelection = useMemo(
    () =>
      beneficiaries.reduce((acc: RowSelectionState, v) => {
        acc[v.id] = assignedBeneficiaries.some((b) => b.id === v.id)
        return acc
      }, {}),
    [beneficiaries, assignedBeneficiaries],
  )

  const onAssignOrUnassign = useCallback(
    ({ assign, beneficiaryId }: { assign: boolean; beneficiaryId: string }) => {
      if (assign) {
        void dispatch(
          assignCaseWorkerToBeneficiary({
            caseWorkerId: caseWorker.id,
            beneficiaryId,
          }),
        )
      } else {
        void dispatch(
          unassignCaseWorkerFromBeneficiary({ caseWorkerId: caseWorker.id, beneficiaryId }),
        )
      }
    },
    [dispatch, caseWorker.id],
  )

  const handleSorting = useCallback(
    (sorting: { id: string; desc: boolean }[]) => {
      const firstItem = sorting[0]
      if (!firstItem) return
      const newSortField = firstItem.id
      const newSortDirection: SortDirection = firstItem.desc ? 'DESC' : 'ASC'
      if (newSortDirection === sortOption.direction && newSortField === sortOption.field) return
      dispatch(setSortOption({ direction: newSortDirection, field: newSortField }))
    },
    [sortOption],
  )

  const initialState = useMemo(
    () => ({
      rowSelection,
      sorting: [{ id: 'fullName', desc: sortOption.direction === 'DESC' }],
    }),
    [sortOption, columns, rowSelection],
  )

  return (
    <DataTable
      key={assignedBeneficiaries.length.toString()}
      columns={columns({ onAssignOrUnassign, onSelectBeneficiary })}
      data={beneficiaries}
      initialState={initialState}
      manualSorting={false}
      onSortingChange={handleSorting}
      footer={(table) => (
        <AssignmentManager table={table} total={totalCount} caseWorkerId={caseWorker.id} />
      )}
    />
  )
}

function AssignmentManager({
  total,
  caseWorkerId,
}: {
  table: Table<Beneficiary>
  total: number
  caseWorkerId: string
}) {
  const dispatch = useStoreDispatch()
  const { tc, t } = useT({ keyPrefix: 'admin.members.assignmentManager' })
  const handleAssignAll = () => {
    void dispatch(assignCaseWorkerToBeneficiariesFromFilters({ caseWorkerId }))
  }
  const handleUnassignAll = () => {
    void dispatch(unassignCaseWorkerFromBeneficiariesFromFilters({ caseWorkerId }))
  }
  return (
    <div className="flex items-end justify-end ">
      <div className="flex gap-2 flex-col border border-primary-100 rounded-md w-fit">
        <InlineCode className="text-center">{tc('beneficiary', { count: total })}</InlineCode>
        <div className="flex items-center justify-end gap-2 px-2 pb-2">
          <Button
            onClick={handleUnassignAll}
            variant="outline"
            isSensitive={{
              title: t('unassignAll.title', { count: total }),
              description: t('unassignAll.description'),
              variant: 'secondary',
            }}
          >
            {tc('unassignAll', { cfl: true })}
          </Button>
          <Button
            onClick={handleAssignAll}
            variant="outline"
            isSensitive={{
              title: t('assignAll.title', { count: total }),
              description: t('assignAll.description'),
              variant: 'secondary',
            }}
          >
            {tc('assignAll', { cfl: true })}
          </Button>
        </div>
      </div>
    </div>
  )
}
